import { Route } from 'vue-router';
import { storeToRefs } from 'pinia';
import { getFromSessionStorage } from '@/apps/order/helper';
import { CONTEXT } from '@/apps/order/routes/ordersCreate';
import {
  useOrdersCreateFormDataStore,
  useOrdersCreateCalculatorStore,
} from '@/apps/order/stores/ordersCreate';

/**
 * Guard to ensure selectedTariffs is set for relocation mode.
 * For relocation, prices are calculated in RelocationView based on the new address.
 * This guard restores selectedTariffs from IDB for reload scenarios.
 * If selectedTariffs is not available, it redirects to the initial relocation route.
 */
const hasRelocationSelectedTariffs = async (to: Route): Promise<void> => (
  new Promise<void>(async (resolve, reject) => {
    // Only apply for relocation mode
    if (to.meta?.mode !== 'relocation') {
      resolve();
      return;
    }

    const ctx = to.meta?.ctx as string || CONTEXT;

    const formDataStore = useOrdersCreateFormDataStore();
    const calculatorStore = useOrdersCreateCalculatorStore();

    const { selectedTariffs: selectedTariffsFormData } = storeToRefs(formDataStore);
    const { selectedTariffs: selectedTariffsCalculator } = storeToRefs(calculatorStore);

    // If selectedTariffs already has data with prices, skip
    const currentTariffs = selectedTariffsFormData.value || {};
    const hasTariffsWithPrices = Object.values(currentTariffs).some(
      (tariff: any) => tariff?.prices_data?.length > 0,
    );
    if (hasTariffsWithPrices) {
      resolve();
      return;
    }

    // Try to get selectedTariffs from IDB (for reload scenarios)
    const idbSelectedTariffs = getFromSessionStorage<Record<string, any>>(
      `${ctx}.selectedTariffs`,
    );
    if (idbSelectedTariffs && Object.keys(idbSelectedTariffs).length > 0) {
      // Check if IDB tariffs have prices
      const idbHasPrices = Object.values(idbSelectedTariffs).some(
        (tariff: any) => tariff?.prices_data?.length > 0,
      );
      if (idbHasPrices) {
        selectedTariffsFormData.value = idbSelectedTariffs;
        selectedTariffsCalculator.value = idbSelectedTariffs;
        resolve();
        return;
      }
    }

    // No valid selectedTariffs with prices - redirect to initial relocation route
    // RelocationView will calculate prices based on the new address
    if (to.name !== 'orders-relocation' && to.params.contractUuid) {
      const error = new Error('to:orders-relocation');
      (error as any).params = { contractUuid: to.params.contractUuid };
      reject(error);
      return;
    }

    resolve();
  })
);

export default hasRelocationSelectedTariffs;
